import { internal } from '@croissants/contracts';
import {
  AccountMenu,
  AppShell,
  PageHeading,
  type PageHeadingBack,
  SidebarLinkContent,
  Wordmark,
} from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import type { InternalStaticKey } from '../i18n/index';
import { useT } from '../i18n/index';
import { useSession } from '../lib/auth';
import { config } from '../lib/env';
import { type Prefetcher, usePrefetch } from '../lib/prefetch';
import { useShellRead } from '../lib/shell-read';
import { useSupportCounts } from '../lib/support';
import { NAV_ICONS, type NavIconName } from './nav-icons';

/**
 * The portal shell: brand, navigation, and who is signed in - the account menu
 * carrying this operator's own settings, their language among them. Nothing
 * here fetches except that preference, and it fetches nothing a panel needs: a
 * failing panel must never take the navigation down with it, because the
 * navigation is how an operator reaches the screen that would tell them why.
 *
 * The layout itself is `AppShell` in `@croissants/ui`; this file is only this
 * portal's configuration of it. The hand-written copy it replaced was a second
 * implementation of the same two-column grid the partner dashboard had, with its
 * own 900px breakpoint, and below that breakpoint it turned the navigation
 * links into a horizontally scrolling strip above the content. The shared shell
 * gives the same links a drawer below `lg` and a collapsible rail above it.
 */

interface NavItem {
  readonly href: string;
  readonly labelKey: InternalStaticKey;
  /** The rail glyph. Required, so a new destination cannot ship as a letter. */
  readonly icon: NavIconName;
  /** Absent from a production build, route and all (see `App.tsx`). */
  readonly demoOnly?: true;
  /**
   * The destination's first read, started when the pointer reaches the entry
   * (`lib/prefetch`), so the click lands on data already on its way. Its
   * arguments are the screen's OWN first call as a rail click opens it - a new
   * history entry, so every filter at its default - and an entry whose first
   * read follows the platform clock or refuses the 30 s cache (`staleTime: 0`)
   * carries none.
   */
  readonly prefetch?: (prefetch: Prefetcher) => void;
}

/**
 * Bakeries and Properties are two destinations, not one filtered screen. They
 * are two different jobs done by two different people - a bakery is a supplier
 * with a share rate and a preparation capacity, a property belongs to a
 * conciergerie or an independent owner and carries QR codes - and "Organizations"
 * was a screen you had to filter before it told you anything.
 */
const NAV: readonly NavItem[] = [
  { href: '/', labelKey: 'nav.dashboard', icon: 'dashboard' },
  /* Alerts' position, and Alerts' successor: one chronological feed over the
     alerts, order events, refusals, audit entries and dead letters that used to
     be five screens with no join between them (OPS-027). */
  { href: '/activity', labelKey: 'nav.activity', icon: 'activity' },
  {
    href: '/orders',
    labelKey: 'nav.orders',
    icon: 'orders',
    /* Every order, latest delivery first: `screens/Orders.tsx`'s defaults. */
    prefetch: (prefetch) =>
      prefetch.pages(
        internal.listOrders,
        { query: { limit: 100, scope: 'ALL', sort: 'SERVICE_DAY', direction: 'DESC' } },
        (response) => response.rows,
      ),
  },
  { href: '/organizations/bakeries', labelKey: 'nav.bakeries', icon: 'bakeries' },
  {
    href: '/organizations/properties',
    labelKey: 'nav.properties',
    icon: 'properties',
    prefetch: (prefetch) =>
      prefetch.query(internal.listOrganizations, {
        query: { limit: 100, type: ['CONCIERGERIE', 'INDEPENDENT_OWNER'] },
      }),
  },
  /* The counter fleet (OPS-029): every paired device, and the builds they are
     offered. Its own entry rather than a Debug section, on the same rule the
     Pilot tab follows - the rail is for the screens that RUN a service, and a
     rollout is a thing an operator drives rather than an incident they look
     into. After the two organization lists because a device belongs to a shop
     that belongs to a house, and before Finance for the same reason. */
  {
    href: '/devices',
    labelKey: 'nav.devices',
    icon: 'devices',
    prefetch: (prefetch) => prefetch.query(internal.listFleetDevices, { query: { limit: 200 } }),
  },
  /* Commercial rates is a sub-tab of Finance, not a sidebar entry of its own -
     see `SubNav` below. `isActive` already lights this entry on `/finance/rates`
     because it matches descendants. */
  /* The cases a person decides and what Stripe was told (issue #2272). Its
     badge is the open-case count. */
  {
    href: '/support',
    labelKey: 'nav.support',
    icon: 'support',
    prefetch: (prefetch) =>
      prefetch.pages(internal.listSupportCases, { query: { status: 'OPEN', limit: 50 } }),
  },
  { href: '/finance', labelKey: 'nav.finance', icon: 'finance' },
  /* The administered platform settings (OPS-025): what the platform SELLS,
     as against the kill switch in Debug, which says whether it is open. It
     sits after Finance because it is the rarest destination in the rail and
     the widest in effect - every other screen is looking at its consequences. */
  {
    href: '/settings',
    labelKey: 'nav.settings',
    icon: 'settings',
    /* `/settings` opens its first tab, Ordering. */
    prefetch: (prefetch) => prefetch.query(internal.getOrderingModes, {}),
  },
  { href: '/quality', labelKey: 'nav.quality', icon: 'quality' },
  {
    href: '/map',
    labelKey: 'nav.map',
    icon: 'map',
    /* The one chunk not preloaded, so here the read also overlaps the chunk. */
    prefetch: (prefetch) =>
      prefetch.query(internal.getCoverage, { query: { storefrontMode: 'A' } }),
  },
  /* Its own entry rather than a Debug section: the Debug rail is for the
     screens that run a service when something is wrong, and this is the
     navigation tool an operator opens constantly during a demo.

     `demoOnly`, and the only entry that carries it: the tab mints a cookie that
     opens the partner and bakery portals AS a tenant, which on the instance
     where the tenants are real people is impersonation (docs/auth-and-access.html
     section 4). The API refuses that cookie in production regardless - this is
     what keeps the rail from offering a destination that cannot work. */
  { href: '/pilot', labelKey: 'nav.pilot', icon: 'pilot', demoOnly: true },
  /* Last, and last on purpose: Health status, Dead letter, Kill switch and
     Audit log are read when something is already wrong or already over, never
     as part of running a service. See `screens/Debug.tsx`. */
  { href: '/debug', labelKey: 'nav.debug', icon: 'debug' },
];

/*
 * `/orders/:id` belongs to Orders, not to the Dashboard. It was the dashboard's
 * while the board was the only list that linked to an order detail; leaving it
 * there once Orders existed would have lit BOTH entries on the same screen.
 */
function isActive(current: string, href: string): boolean {
  if (href === '/') return current === '/';
  return current === href || current.startsWith(`${href}/`);
}

export function Shell({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  const session = useSession();
  /* First, before any reader below exists: the clock, the ordering switches
     and this rail's badge arrive as ONE request on the first load
     (`lib/shell-read`), and each reader joins that fetch. */
  useShellRead();
  /* no-eternal-spinner: rendered by screens/Support.tsx, which owns this
     query's failure. The rail draws a count while there is one and nothing
     otherwise - it never waits on the read. */
  const openCases = useSupportCounts().data?.open ?? 0;
  const [location] = useLocation();
  const prefetch = usePrefetch();

  return (
    <AppShell
      mainId="internal-main"
      navLabel={t('app.shortTitle')}
      labels={{
        skipToContent: t('app.skipToContent'),
        openMenu: t('app.openMenu'),
        closeMenu: t('app.closeMenu'),
        collapseSidebar: t('app.collapseSidebar'),
        expandSidebar: t('app.expandSidebar'),
      }}
      brand={
        <Link href="/" className="cib-topbar__brand" aria-label={t('app.title')}>
          <Wordmark size={34} title={t('app.shortTitle')} />
        </Link>
      }
      topBarActions={
        /* The same control the partner portal shows, in the same corner.
           The two portals do not share a user pool - two Clerk
           applications, one per signed-in staff portal - so the component takes
           an identity rather than reading one. */
        <AccountMenu
          name={session.label ?? t('app.accountUnknown')}
          imageUrl={session.imageUrl}
          onSignOut={() => session.signOut()}
          onOpenSettings={session.openAccount ?? undefined}
          labels={{
            account: t('app.account'),
            signOut: t('app.signOut'),
            settings: t('app.accountSettings'),
            signedInAs: t('app.signedInHint'),
          }}
        />
      }
      /* One filter, applied where the rail is built rather than at each entry,
         so a `demoOnly` destination cannot appear in production by being added
         somewhere else in this list. `App.tsx` drops the matching ROUTE on the
         same build, so a remembered URL lands on Not found rather than on a
         screen the rail is hiding. */
      nav={NAV.filter((item) => !(item.demoOnly && config.isProductionInstance)).map((item) => {
        const active = isActive(location, item.href);
        const warm = item.prefetch === undefined ? undefined : () => item.prefetch?.(prefetch);
        return (
          <Link
            key={item.href}
            href={item.href}
            className="cib-sidebar__link"
            onMouseEnter={warm}
            onFocus={warm}
            {...(active ? { 'aria-current': 'page' as const } : {})}
          >
            <SidebarLinkContent label={t(item.labelKey)} icon={NAV_ICONS[item.icon]} />
            {item.href === '/support' && openCases > 0 ? (
              <span className="internal-nav-count">{openCases}</span>
            ) : null}
          </Link>
        );
      })}
    >
      {children}
    </AppShell>
  );
}

/**
 * The sub-navigation for a section that owns more than one screen. It sits
 * above the active screen's `ScreenHeader`, so the strip names the section's
 * destinations and the heading immediately below names the one you are on.
 *
 * These are links, not `role="tab"` buttons. Each entry is a real route with a
 * URL an operator can paste into an incident thread, and `aria-current="page"`
 * is what says which one is open. Announcing them as tabs would promise a
 * widget whose panels are all present in the document, which is not what a
 * route change does.
 *
 * Matching is exact, unlike the sidebar's: the section's own index route
 * (`/finance`) is itself one of the entries, so the prefix match the sidebar
 * uses to light Finance on `/finance/rates` would light both entries here.
 */
export function SubNav({
  label,
  items,
}: {
  label: string;
  /** `current` overrides the exact match: a tab that owns the records under it. */
  items: readonly { readonly href: string; readonly label: string; readonly current?: boolean }[];
}): JSX.Element {
  const [location] = useLocation();
  return (
    <nav className="internal-subnav" aria-label={label}>
      {items.map((item) => {
        const active = item.current ?? location === item.href;
        return (
          <Link
            key={item.href}
            href={item.href}
            className="internal-subnav__link"
            {...(active ? { 'aria-current': 'page' as const } : {})}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * One screen's heading row: the way back, the name, its (i), and the actions.
 *
 * This portal's configuration of `PageHeading` in `@croissants/ui` and nothing
 * else - the arrow, the title's size and the (i) are decided there, once, for
 * every portal. It supplies two things only this app knows: `wouter`'s `Link`,
 * so the arrow is a route change rather than a page load, and the accessible
 * name of the (i) in the operator's own language.
 */
export function ScreenHeader({
  title,
  titleInTab,
  info,
  back,
  lead,
  actions,
}: {
  title: ReactNode;
  /** The active sub-tab already says this: keep the heading, stop drawing it. */
  titleInTab?: boolean;
  /** The screen's one line of purpose, already translated. Goes behind the (i). */
  info?: string;
  /**
   * The list this record belongs to, and what the arrow is called. Only the
   * fallback: the arrow returns to the page the reader came from when there is
   * one (`PageHeadingBack`). Already translated.
   */
  back?: PageHeadingBack;
  /** A line beside the name that has to be clickable, so it cannot be a tooltip. */
  lead?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  const t = useT();
  return (
    <PageHeading
      title={title}
      linkAs={Link}
      infoLabel={t('action.about')}
      {...(titleInTab === undefined ? {} : { titleInTab })}
      {...(info === undefined ? {} : { info })}
      {...(back === undefined ? {} : { back })}
      {...(lead === undefined ? {} : { lead })}
      {...(actions === undefined ? {} : { actions })}
    />
  );
}
